/**
 * CrmPipelines - Página de gerenciamento de pipelines CRM
 * 
 * Permite criar, editar e gerenciar pipelines e suas fases
 * dentro de cada workspace do CRM.
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  GitBranch, 
  MoreVertical, 
  Edit, 
  Trash2,
  ChevronRight,
  Layers,
  Settings2,
  GripVertical
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
import { PipelinePhaseManager } from '@/components/crm/PipelinePhaseManager';
import { usePermissions } from '@/hooks/usePermissions';

// Logger
const logger = createLogger('CrmPipelines');

// Interfaces
interface CrmWorkspace {
  id: string;
  name: string;
  color: string;
}

interface CrmPipelinePhase {
  id: string;
  name: string;
  color: string | null;
  icon: string | null;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
  probability_percent: number | null;
  auto_move_days?: number | null;
}

interface CrmPipeline {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  workspace?: CrmWorkspace;
  phases?: CrmPipelinePhase[];
  phases_count?: number;
  deals_count?: number;
}

export default function CrmPipelines() {
  const { t } = useTranslation('crm');
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();

  // === Controle de permissões granular (compartilha com workspaces) ===
  const { canCreate, canEdit, canDelete } = usePermissions();
  const MENU_KEY = 'crm_workspaces';
  const hasCreatePermission = canCreate(MENU_KEY);
  const hasEditPermission = canEdit(MENU_KEY);
  const hasDeletePermission = canDelete(MENU_KEY);
  
  // Estados
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPhasesDialogOpen, setIsPhasesDialogOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState<CrmPipeline | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    workspace_id: '',
    is_default: false
  });

  // Query para buscar workspaces
  const { data: workspaces } = useQuery({
    queryKey: ['crm-workspaces', currentTenant?.id],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      
      // Busca via RPC server-side (reutiliza RPC existente)
      const { data, error } = await supabase.rpc(
        'get_crm_workspaces_with_stats',
        { p_tenant_hint: currentTenant.id }
      );
      
      if (error) throw error;
      // Mapeia resposta RPC para interface CrmWorkspace (id, name, color)
      return (data || [])
        .filter((w: any) => w.is_active)
        .map((w: any) => ({ id: w.id, name: w.name, color: w.color || '#3B82F6' })) as CrmWorkspace[];
    },
    enabled: !!currentTenant?.id
  });

  // Query para buscar pipelines
  const { data: pipelines, isLoading } = useQuery({
    queryKey: ['crm-pipelines', currentTenant?.id, selectedWorkspaceId],
    queryFn: async () => {
      if (!currentTenant?.id) return [];
      
      let query = supabase
        .from('crm_pipelines')
        .select(`
          *,
          workspace:crm_workspaces(id, name, color),
          phases:crm_pipeline_phases(id, name, color, icon, sort_order, is_won, is_lost, probability_percent)
        `)
        .eq('tenant_id', currentTenant.id)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      
      if (selectedWorkspaceId !== 'all') {
        query = query.eq('workspace_id', selectedWorkspaceId);
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      // Ordenar fases por sort_order
      return (data || []).map(pipeline => ({
        ...pipeline,
        phases: (pipeline.phases || []).sort((a: CrmPipelinePhase, b: CrmPipelinePhase) => a.sort_order - b.sort_order),
        phases_count: pipeline.phases?.length || 0
      })) as CrmPipeline[];
    },
    enabled: !!currentTenant?.id
  });

  // Mutation para criar pipeline
  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!currentTenant?.id) throw new Error('Tenant não encontrado');
      
      // Criar pipeline
      const { data: pipeline, error: pipelineError } = await supabase
        .from('crm_pipelines')
        .insert({
          tenant_id: currentTenant.id,
          workspace_id: data.workspace_id,
          name: data.name,
          description: data.description || null,
          is_default: data.is_default
        })
        .select()
        .single();
      
      if (pipelineError) throw pipelineError;

      // Criar fases padrão
      const defaultPhases = [
        { name: 'Novo Lead', color: '#94A3B8', sort_order: 0, probability_percent: 10 },
        { name: 'Qualificação', color: '#60A5FA', sort_order: 1, probability_percent: 25 },
        { name: 'Proposta', color: '#FBBF24', sort_order: 2, probability_percent: 50 },
        { name: 'Negociação', color: '#F97316', sort_order: 3, probability_percent: 75 },
        { name: 'Ganho', color: '#22C55E', sort_order: 4, probability_percent: 100, is_won: true },
        { name: 'Perdido', color: '#EF4444', sort_order: 5, probability_percent: 0, is_lost: true }
      ];

      const { error: phasesError } = await supabase
        .from('crm_pipeline_phases')
        .insert(
          defaultPhases.map(phase => ({
            pipeline_id: pipeline.id,
            tenant_id: currentTenant.id,
            ...phase
          }))
        );

      if (phasesError) {
        logger.error('Erro ao criar fases padrão', phasesError);
      }

      return pipeline;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success(t('pipelines.created', 'Pipeline criado com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao criar pipeline', error);
      toast.error(error.message || t('pipelines.createError', 'Erro ao criar pipeline'));
    }
  });

  // Mutation para atualizar pipeline
  const updateMutation = useMutation({
    mutationFn: async (data: { id: string } & typeof formData) => {
      const { data: result, error } = await supabase
        .from('crm_pipelines')
        .update({
          name: data.name,
          description: data.description || null,
          workspace_id: data.workspace_id,
          is_default: data.is_default
        })
        .eq('id', data.id)
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      setIsEditDialogOpen(false);
      setSelectedPipeline(null);
      toast.success(t('pipelines.updated', 'Pipeline atualizado com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao atualizar pipeline', error);
      toast.error(error.message || t('pipelines.updateError', 'Erro ao atualizar pipeline'));
    }
  });

  // Mutation para deletar pipeline
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_pipelines')
        .update({ is_active: false })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
      setIsDeleteDialogOpen(false);
      setSelectedPipeline(null);
      toast.success(t('pipelines.deleted', 'Pipeline removido com sucesso!'));
    },
    onError: (error: any) => {
      logger.error('Erro ao deletar pipeline', error);
      toast.error(error.message || t('pipelines.deleteError', 'Erro ao remover pipeline'));
    }
  });

  // Handlers
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      workspace_id: workspaces?.[0]?.id || '',
      is_default: false
    });
  };

  const handleCreate = () => {
    if (!formData.name.trim()) {
      toast.error(t('pipelines.nameRequired', 'Nome é obrigatório'));
      return;
    }
    if (!formData.workspace_id) {
      toast.error(t('pipelines.workspaceRequired', 'Selecione um workspace'));
      return;
    }
    createMutation.mutate(formData);
  };

  const handleEdit = (pipeline: CrmPipeline) => {
    setSelectedPipeline(pipeline);
    setFormData({
      name: pipeline.name,
      description: pipeline.description || '',
      workspace_id: pipeline.workspace_id,
      is_default: pipeline.is_default
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = () => {
    if (!selectedPipeline || !formData.name.trim()) return;
    updateMutation.mutate({
      id: selectedPipeline.id,
      ...formData
    });
  };

  const handleDelete = (pipeline: CrmPipeline) => {
    setSelectedPipeline(pipeline);
    setIsDeleteDialogOpen(true);
  };

  const handleManagePhases = (pipeline: CrmPipeline) => {
    setSelectedPipeline(pipeline);
    setIsPhasesDialogOpen(true);
  };

  const confirmDelete = () => {
    if (!selectedPipeline) return;
    deleteMutation.mutate(selectedPipeline.id);
  };

  const openCreateDialog = () => {
    resetForm();
    if (workspaces?.length) {
      setFormData(prev => ({ ...prev, workspace_id: workspaces[0].id }));
    }
    setIsCreateDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <GitBranch className="h-6 w-6" />
            {t('pipelines.title', 'Pipelines')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Select value={selectedWorkspaceId} onValueChange={setSelectedWorkspaceId}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder={t('pipelines.filterWorkspace', 'Filtrar workspace')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('pipelines.allWorkspaces', 'Todos workspaces')}</SelectItem>
              {workspaces?.map(ws => (
                <SelectItem key={ws.id} value={ws.id}>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ws.color }} />
                    {ws.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {hasCreatePermission && (
            <Button onClick={openCreateDialog} disabled={!workspaces?.length}>
              <Plus className="h-4 w-4 mr-2" />
              {t('pipelines.create', 'Novo Pipeline')}
            </Button>
          )}
        </div>
      </div>

      {/* Aviso se não há workspaces */}
      {!workspaces?.length && !isLoading && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Layers className="h-12 w-12 text-muted-foreground/20 mb-4" />
            <h3 className="text-lg font-medium">
              {t('pipelines.noWorkspaces', 'Nenhum workspace disponível')}
            </h3>
            <p className="text-muted-foreground text-center max-w-sm mt-1">
              {t('pipelines.createWorkspaceFirst', 'Crie um workspace primeiro para poder criar pipelines.')}
            </p>
            <Button className="mt-4" variant="outline" onClick={() => window.location.href = '/tenant/crm/workspaces'}>
              <Layers className="h-4 w-4 mr-2" />
              {t('pipelines.goToWorkspaces', 'Ir para Workspaces')}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Lista de Pipelines */}
      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  {[1, 2, 3, 4].map(j => (
                    <Skeleton key={j} className="h-8 w-24 rounded-full" />
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : pipelines && pipelines.length > 0 ? (
        <Accordion type="multiple" className="space-y-4">
          {pipelines.map(pipeline => (
            <AccordionItem key={pipeline.id} value={pipeline.id} className="border rounded-lg bg-card">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <GitBranch className="h-5 w-5 text-muted-foreground" />
                    <div className="text-left">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{pipeline.name}</span>
                        {pipeline.is_default && (
                          <Badge variant="secondary" className="text-xs">
                            {t('pipelines.default', 'Padrão')}
                          </Badge>
                        )}
                      </div>
                      {pipeline.workspace && (
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          <div 
                            className="w-2 h-2 rounded-full" 
                            style={{ backgroundColor: pipeline.workspace.color }} 
                          />
                          {pipeline.workspace.name}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs">
                      {pipeline.phases_count} {t('pipelines.phases', 'fases')}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {hasEditPermission && (
                          <DropdownMenuItem onClick={() => handleManagePhases(pipeline)}>
                            <Settings2 className="h-4 w-4 mr-2" />
                            {t('pipelines.managePhases', 'Gerenciar Fases')}
                          </DropdownMenuItem>
                        )}
                        {hasEditPermission && (
                          <DropdownMenuItem onClick={() => handleEdit(pipeline)}>
                            <Edit className="h-4 w-4 mr-2" />
                            {t('common.edit', 'Editar')}
                          </DropdownMenuItem>
                        )}
                        {hasDeletePermission && (
                          <DropdownMenuItem 
                            onClick={() => handleDelete(pipeline)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            {t('common.delete', 'Excluir')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                {pipeline.description && (
                  <p className="text-sm text-muted-foreground mb-4">
                    {pipeline.description}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {pipeline.phases?.map((phase, index) => (
                    <div 
                      key={phase.id} 
                      className="flex items-center gap-1"
                    >
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1.5"
                        style={{ 
                          borderColor: phase.color || undefined,
                          backgroundColor: phase.color ? `${phase.color}15` : undefined
                        }}
                      >
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: phase.color || '#94A3B8' }} 
                        />
                        {phase.name}
                        {phase.probability_percent !== null && (
                          <span className="text-xs opacity-60">
                            {phase.probability_percent}%
                          </span>
                        )}
                      </Badge>
                      {index < (pipeline.phases?.length || 0) - 1 && (
                        <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleManagePhases(pipeline)}
                  >
                    <Settings2 className="h-4 w-4 mr-2" />
                    {t('pipelines.configurePhases', 'Configurar Fases')}
                  </Button>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      ) : workspaces?.length ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <GitBranch className="h-12 w-12 text-muted-foreground/20 mb-4" />
            <h3 className="text-lg font-medium">
              {t('pipelines.empty', 'Nenhum pipeline criado')}
            </h3>
            <p className="text-muted-foreground text-center max-w-sm mt-1">
              {t('pipelines.emptyDescription', 'Crie seu primeiro pipeline para começar a gerenciar suas oportunidades de vendas.')}
            </p>
            <Button className="mt-4" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              {t('pipelines.createFirst', 'Criar Primeiro Pipeline')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Dialog de Criação */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pipelines.createTitle', 'Novo Pipeline')}</DialogTitle>
            <DialogDescription>
              {t('pipelines.createDescription', 'Crie um pipeline com fases padrão que você pode personalizar depois.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace">{t('pipelines.workspace', 'Workspace')}</Label>
              <Select 
                value={formData.workspace_id} 
                onValueChange={v => setFormData(prev => ({ ...prev, workspace_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('pipelines.selectWorkspace', 'Selecione um workspace')} />
                </SelectTrigger>
                <SelectContent>
                  {workspaces?.map(ws => (
                    <SelectItem key={ws.id} value={ws.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ws.color }} />
                        {ws.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('pipelines.name', 'Nome')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('pipelines.namePlaceholder', 'Ex: Vendas B2B')}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('pipelines.description', 'Descrição')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('pipelines.descriptionPlaceholder', 'Descreva o propósito deste pipeline...')}
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_default">{t('pipelines.setAsDefault', 'Pipeline padrão')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('pipelines.defaultDescription', 'Novos negócios serão criados neste pipeline')}
                </p>
              </div>
              <Switch
                id="is_default"
                checked={formData.is_default}
                onCheckedChange={v => setFormData(prev => ({ ...prev, is_default: v }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleCreate} disabled={createMutation.isPending}>
              {createMutation.isPending ? t('common.creating', 'Criando...') : t('common.create', 'Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Edição */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('pipelines.editTitle', 'Editar Pipeline')}</DialogTitle>
            <DialogDescription>
              {t('pipelines.editDescription', 'Atualize as informações do pipeline.')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-workspace">{t('pipelines.workspace', 'Workspace')}</Label>
              <Select 
                value={formData.workspace_id} 
                onValueChange={v => setFormData(prev => ({ ...prev, workspace_id: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {workspaces?.map(ws => (
                    <SelectItem key={ws.id} value={ws.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: ws.color }} />
                        {ws.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">{t('pipelines.name', 'Nome')}</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">{t('pipelines.description', 'Descrição')}</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="edit-is_default">{t('pipelines.setAsDefault', 'Pipeline padrão')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('pipelines.defaultDescription', 'Novos negócios serão criados neste pipeline')}
                </p>
              </div>
              <Switch
                id="edit-is_default"
                checked={formData.is_default}
                onCheckedChange={v => setFormData(prev => ({ ...prev, is_default: v }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button onClick={handleUpdate} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? t('common.saving', 'Salvando...') : t('common.save', 'Salvar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('pipelines.deleteTitle', 'Excluir Pipeline')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('pipelines.deleteDescription', 'Tem certeza que deseja excluir o pipeline "{name}"? Todos os negócios associados serão movidos para o pipeline padrão.', { name: selectedPipeline?.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t('common.deleting', 'Excluindo...') : t('common.delete', 'Excluir')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de Gerenciamento de Fases */}
      <Dialog open={isPhasesDialogOpen} onOpenChange={setIsPhasesDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {t('pipelines.phasesTitle', 'Fases do Pipeline')}: {selectedPipeline?.name}
            </DialogTitle>
            <DialogDescription>
              {t('pipelines.phasesDescription', 'Configure as fases do seu pipeline de vendas. Arraste para reordenar.')}
            </DialogDescription>
          </DialogHeader>
          {selectedPipeline && (
            <PipelinePhaseManager 
              pipelineId={selectedPipeline.id}
              tenantId={currentTenant?.id || ''}
              onClose={() => {
                setIsPhasesDialogOpen(false);
                queryClient.invalidateQueries({ queryKey: ['crm-pipelines'] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
